import AppleHealthChartsWrapper from 'pages/Dashboard/components/AppleHealthChartsWrapper';
import AppleHealthSleepChart from 'pages/Dashboard/components/AppleHealthSleepChart';
import { getChartDateRanges, TimeFrameOptions } from 'pages/Dashboard/utils/trackingUtils';
import React from 'react';
import { MixpanelEventName } from 'utils/constants';
import { trackMixpanelEvent } from 'utils/utilMethods';
import AppleHealthSleepDialog from 'pages/Dashboard/components/AppleHealthSleepDialog';
import { SleepCategory, ListWaypointDetails, WaypointSource, WaypointTrackingAPIResponse } from 'pages/Dashboard/types/waypoints.types';
import { UseQueryResult } from 'react-query';

interface SleepComponentProps {
  timeFrame: TimeFrameOptions;
  patientId: string;
  title: string;
  onChangeTimeFrame: (v: string) => void;
  averages: { id: SleepCategory; absoluteValue: number }[];
  previousPeriodAverages: { id: SleepCategory; absoluteValue: number }[];
  onChangeSource: (id: string) => void;
  sourceId: string;
  sourceOptions: WaypointSource[];
  isLoading: boolean;
  sourceObject?: Partial<WaypointSource>;
  fetchPatientWaypointTrackingResult?: UseQueryResult<
    ListWaypointDetails | WaypointTrackingAPIResponse,
    unknown
  >;
  hasData: boolean | undefined;
  gridSize?: number;
}

function SleepComponent({
  timeFrame,
  patientId,
  title,
  onChangeTimeFrame,
  averages,
  previousPeriodAverages,
  onChangeSource,
  sourceId,
  sourceOptions,
  isLoading,
  sourceObject,
  fetchPatientWaypointTrackingResult,
  hasData,
  gridSize = 6,
}: SleepComponentProps) {
  const yAxisProps = getChartDateRanges(timeFrame as TimeFrameOptions) as {
    startTime: number;
    endTime: number;
    ticks: number[];
    isInDays: boolean;
  };
  const [isDrawerOpen, setIsDrawerOpen] = React.useState('');
  const closeDrawer = React.useCallback(() => {
    setIsDrawerOpen('');
  }, []);

  const openDrawer = React.useCallback(() => {
    trackMixpanelEvent(
      MixpanelEventName.PATIENT_DETAILS_AH_SLEEP_CHART_VIEW_DETAILS_BUTTON_CLICKED,
      {
        'patient id': patientId ?? '',
        location: 'progress_monitor_tab_sleep_view_details',
      },
      true,
    );
    setIsDrawerOpen('show');
  }, [patientId]);

  return (
    <>
      <AppleHealthChartsWrapper
        title={title}
        isLoading={isLoading}
        onViewDetails={openDrawer}
        showViewDetails={averages?.length > 0}
        sourceObject={sourceObject}
        timeFrame={timeFrame}
        onTimeFrameChange={onChangeTimeFrame}
        noData={!hasData}
        gridSize={gridSize}
        renderChart={(
          <AppleHealthSleepChart
            timeFrame={timeFrame}
            averages={averages}
            previousPeriodAverages={previousPeriodAverages}
            setSourceId={onChangeSource}
            sourceId={sourceId}
            sourceOptions={sourceOptions.map((src) => ({
              label: src.label || src.sourceName,
              value: src.value || src.sourceId,
              isSelected: src.isSelected,
            }))}
          />
        )}
      />
      <AppleHealthSleepDialog
        isOpen={!!isDrawerOpen}
        onClose={closeDrawer}
        timeFrame={timeFrame}
        yAxisProps={yAxisProps}
        onChangeTimeFrame={onChangeTimeFrame}
        sourceId={sourceId}
        isLoading={isLoading}
        fetchPatientWaypointTrackingResult={
          fetchPatientWaypointTrackingResult as { data: ListWaypointDetails }
        }
        onChangeSource={onChangeSource}
        sourceOptions={sourceOptions}
      />
    </>
  );
}

export default SleepComponent;
